import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
    Chip, Stack, CircularProgress,
} from '@mui/material';
import { WorkSiteService } from '../../services/workSite.service';
import { WorkSite } from '../../interfaces/workSite.interface';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import { useToast } from '../../context/ToastContext';
import { displayUsername } from '../../utils/userUtils';

interface WorkSiteAssignmentDialogProps {
    open: boolean;
    site: WorkSite;
    onClose: () => void;
}

interface Assignee {
    id: string;
    label: string;
}

const assigneeLabel = (username: string | null, fullName: string | null, userId: string): string => {
    if (fullName) return `${fullName} (${displayUsername(username ?? '')})`;
    if (username) return displayUsername(username);
    return userId;
};

const WorkSiteAssignmentDialog: React.FC<WorkSiteAssignmentDialogProps> = ({ open, site, onClose }) => {
    const [assignees, setAssignees] = useState<Assignee[]>([]);
    const [pendingEmployee, setPendingEmployee] = useState<EmployeeOption | null>(null);
    const [loading, setLoading] = useState(true);
    // Never let this dialog submit a set it did not successfully load: a failed load
    // must not silently present as "0 employees assigned" with Save still enabled —
    // that would let an admin add one person and wipe everyone else's assignment.
    const [loadFailed, setLoadFailed] = useState(false);
    const [saving, setSaving] = useState(false);
    const toast = useToast();

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        setLoadFailed(false);
        WorkSiteService.listAssignees(site.id!!)
            .then(rows => {
                if (cancelled) return;
                setAssignees(rows.map(row => ({
                    id: row.userId,
                    label: assigneeLabel(row.username, row.fullName, row.userId),
                })));
            })
            .catch(err => {
                if (cancelled) return;
                setLoadFailed(true);
                toast.showApiError(err, 'Failed to load assigned employees');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, site.id]);

    const handleAdd = () => {
        if (!pendingEmployee) return;
        setAssignees(prev => {
            if (prev.some(a => a.id === pendingEmployee.id)) {
                return prev;
            }
            // Same labelling as the loaded rows. Labelling a freshly added assignee with the
            // username alone meant the same person's chip read differently before and after
            // save-and-reopen.
            return [...prev, {
                id: pendingEmployee.id,
                label: assigneeLabel(pendingEmployee.username, pendingEmployee.fullName ?? null, pendingEmployee.id),
            }];
        });
        setPendingEmployee(null);
    };

    const handleRemove = (id: string) => {
        setAssignees(prev => prev.filter(a => a.id !== id));
    };

    const handleSave = async () => {
        if (loadFailed) return;
        setSaving(true);
        try {
            const ids = Array.from(new Set(assignees.map(a => a.id)));
            await WorkSiteService.replaceAssignees(site.id!!, ids);
            toast.showSuccess('Assignments updated');
            onClose();
        } catch (err) {
            toast.showApiError(err, 'Failed to update assignments');
        } finally {
            setSaving(false);
        }
    };

    const disableEditing = loading || loadFailed || saving;

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'attendance.assignForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>Assign employees — {site.name}</DialogTitle>
            <DialogContent data-help-section="attendance.assignForm.section.details">
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Employees with no site assignment are measured against every active site in this
                    institution. Assign employees here only when they must punch at this site
                    specifically.
                </Typography>

                {loadFailed && (
                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                        Could not load the current assignments for this site. Close and reopen this
                        dialog to try again — saving is disabled so an incomplete list can't
                        overwrite the real one.
                    </Typography>
                )}

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'flex-start', mb: 2 }}>
                    <EmployeeAutocomplete
                        data-help="attendance.assignForm.field.employee"
                        value={pendingEmployee}
                        onChange={setPendingEmployee}
                        disabled={disableEditing}
                        sx={{ flex: '1 1 260px', maxWidth: 400 }}
                    />
                    <Button data-help="attendance.assignForm.add" variant="outlined" onClick={handleAdd} disabled={!pendingEmployee || disableEditing}>
                        Add
                    </Button>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <CircularProgress size={16} />
                        <Typography variant="subtitle2">Loading current assignments…</Typography>
                    </Box>
                ) : (
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {assignees.length} {assignees.length === 1 ? 'employee' : 'employees'} assigned
                    </Typography>
                )}

                {!loading && !loadFailed && assignees.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        No specific assignees — this site's employees are measured against every
                        active site in this institution, same as everyone else.
                    </Typography>
                )}

                <Stack direction="row" flexWrap="wrap" gap={1}>
                    {assignees.map(a => (
                        <Chip
                            key={a.id}
                            data-help="attendance.assignForm.chip"
                            label={a.label}
                            onDelete={() => handleRemove(a.id)}
                            disabled={disableEditing}
                            data-testid={`assignee-chip-${a.id}`}
                        />
                    ))}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-help="attendance.assignForm.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="attendance.assignForm.save" variant="contained" onClick={handleSave} disabled={disableEditing}>
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default WorkSiteAssignmentDialog;
